import styled from '@emotion/styled'
import { PictureOutlined } from '@ant-design/icons'
import { useFormikContext } from 'formik'
import dayjs from 'dayjs'
import { useSelector } from 'src/hooks/useRedux'
import type { WizardFormValues } from '@sportninja/common/features/competition/wizard'
import {
  getSports,
  getAllGenericSportProperties,
} from '@sportninja/common/selectors/types'
import {
  COMPETITION_TYPE_OPTIONS,
  STRUCTURE_TYPE_OPTIONS,
  JOIN_TYPE_OPTIONS,
  isCloneShortCircuit,
} from '@sportninja/common/features/competition/wizard'
import Text from 'src/components/ts-components/Text'
import { competitionWord } from '@sportninja/common/features/competition/wizard'
import colors from '@sportninja/common/constants/appColors'

const Row = ({ label, value }: { label: string; value: string }) => (
  <RowContainer>
    <Text variant='paragraphSmall' color={colors.NEUTRAL_50}>
      {label}
    </Text>
    <Text variant='paragraphSmall' weight='medium' color={colors.WHITE}>
      {value}
    </Text>
  </RowContainer>
)

const Step6Summary = () => {
  const { values } = useFormikContext<WizardFormValues>()
  const sports = useSelector((state: any) => Object.values(getSports(state)))
  const genericSports = useSelector((state: any) =>
    getAllGenericSportProperties(state)
  )

  const sportName = (() => {
    if (values.info.genericSportId) {
      const g = (genericSports ?? []).find(
        (s: any) => s?.id === values.info.genericSportId
      ) as { name?: string } | undefined
      return g?.name ?? null
    }
    if (values.info.sportId) {
      const s = sports.find((x: any) => x?.id === values.info.sportId) as
        | { name_full?: string }
        | undefined
      return s?.name_full ?? null
    }
    return null
  })()

  const typeLabel =
    COMPETITION_TYPE_OPTIONS.find((o) => o.value === values.competitionType)
      ?.label ?? ''
  const structureLabel =
    STRUCTURE_TYPE_OPTIONS.find((o) => o.value === values.structureType)
      ?.label ?? ''
  // Simple Playoffs (no source) skips the Join step but submits as 'manual';
  // surface that on the summary so the row isn't a dash.
  const effectiveJoinType =
    values.joinType ??
    (values.competitionType === 'playoffs_simple' &&
    !values.cloneSource.sourceScheduleId
      ? 'manual'
      : null)
  const joinLabel =
    JOIN_TYPE_OPTIONS.find((o) => o.value === effectiveJoinType)?.label ?? ''

  const dates =
    values.info.startsAt && values.info.endsAt
      ? `${dayjs(values.info.startsAt).format('MMM D, YYYY')} → ${dayjs(
          values.info.endsAt
        ).format('MMM D, YYYY')}`
      : ''

  return (
    <Container>
      <Header>
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          Review Your {competitionWord()}
        </Text>
        <Text variant='paragraphMedium' color={colors.NEUTRAL_50}>
          Everything looks good? Hit Create to launch your{' '}
          {competitionWord({ capitalize: false })}.
        </Text>
      </Header>
      <Card>
        <TitleStrip>
          <IconSquare>
            <PictureOutlined style={{ color: colors.WHITE, fontSize: 20 }} />
          </IconSquare>
          <TitleText>
            <Text variant='heading6' weight='semiBold' color={colors.WHITE}>
              {values.info.name || `${competitionWord()} Name`}
            </Text>
            <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
              {sportName ?? '—'}
            </Text>
          </TitleText>
        </TitleStrip>
        <Rows>
          <Row label='Start/End Date' value={dates || '—'} />
          <Row label={`${competitionWord()} Format`} value={typeLabel || '—'} />
          {isCloneShortCircuit(values) ? (
            <Row
              label='Cloning from'
              value={values.cloneSource.sourceName || '—'}
            />
          ) : (
            <>
              <Row label='Division Format' value={structureLabel || '—'} />
              <Row label='Setup' value={joinLabel || '—'} />
            </>
          )}
        </Rows>
      </Card>
    </Container>
  )
}

export default Step6Summary

// ---- styled ----
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`

const Card = styled.div`
  width: 100%;
  max-width: 380px;
  background: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_300};
  border-radius: 12px;
  overflow: hidden;
`

const TitleStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: ${colors.SECONDARY_300};
`

const IconSquare = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${colors.PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const TitleText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const Rows = styled.div`
  padding: 0 16px;
`

const RowContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0 17px;
  border-bottom: 1px solid ${colors.SECONDARY_300};

  &:last-child {
    border-bottom: 0;
  }
`
